/* eslint-disable @typescript-eslint/no-explicit-any */
import { store } from '@stores';
import {
  ShortFormLeadResponse,
  EventsEnum,
  EngagementInfo,
  IssueDetectedVariant,
  LeadType,
} from '@types';
import {
  compileBaselineEngagementData,
  OfflineDefaultFeatureFlags,
  sendEventToHost,
} from '@utils';

import { retryWithBackoff, isRetryableError } from '../../utils/retry';
import { CloudFunctionsService } from '../CloudFunctions';
import { FirebaseService } from '../Firebase';
import { GTMService } from '../GTM';
import { rudderstackService } from '../Rudderstack';

function determineMapiErrorVariant(error: any): IssueDetectedVariant {
  const errors = error?.details?.Errors;
  if (Array.isArray(errors)) {
    if (errors.includes('Duplicate Lead'))
      return IssueDetectedVariant.DUPLICATE_LEAD;
    if (errors.includes('Do Not Contact Lead'))
      return IssueDetectedVariant.FALLBACK;
  }
  return IssueDetectedVariant.FALLBACK;
}

export class EventService {
  private cloudFunctionsService = new CloudFunctionsService();
  private firebaseService = new FirebaseService(this.cloudFunctionsService);
  private gtm = GTMService.getInstance();
  private engagementEmitter = new EventTarget();

  private eventToFunction: Record<EventsEnum, () => Promise<void>>;

  constructor() {
    this.eventToFunction = {
      [EventsEnum.SEND_EVALUATION]:
        this.sendEngagementToDecisionEngine.bind(this),
      [EventsEnum.SUBMIT_SHORT_FORM]: this.submitShortForm.bind(this),
    };
  }

  public async callEvents(events?: string[]) {
    const eventsResult = [];
    for (const e of events || []) {
      if (store.commonStore.hasMapiError) break;

      if (Object.keys(this.eventToFunction).includes(e)) {
        eventsResult.push(
          await this.eventToFunction[e as keyof typeof EventsEnum]()
        );
      }
    }
    if (
      events?.includes(EventsEnum.SUBMIT_SHORT_FORM) &&
      !store.commonStore.hasMapiError
    ) {
      store.commonStore.sendConversionToHost(
        store.decisionEngineStore.isUserUnqualified ? 'DNQ' : undefined
      );
    }
    return eventsResult;
  }

  public async invokeSweeper() {
    return this.cloudFunctionsService.invokeSweeper();
  }

  public async getFirebaseRemoteConfig() {
    try {
      return this.firebaseService.getFirebaseRemoteConfig('module_order');
    } catch {
      return OfflineDefaultFeatureFlags;
    }
  }

  public async getFirebaseFeatureFlags() {
    try {
      return this.firebaseService.getFirebaseRemoteConfig('feature_flags');
    } catch {
      return OfflineDefaultFeatureFlags;
    }
  }

  public async sendEngagementToDecisionEngine() {
    await store.decisionEngineStore.sendEngagementToDecisionEngine();
  }

  public async submitShortForm() {
    const partner = store.partnerStore.partner!;
    await this.waitForEngagementCreation();
    if (partner.mode == 'live') {
      try {
        store.commonStore.setButtonLoading(true);
        this.gtm.sendGTMDataLayerContactInfoReceived(partner);

        const { success, result, error, attempts } = await retryWithBackoff(
          () => this.cloudFunctionsService.submitShortFormLead(),
          isRetryableError
        );

        if (success && result) {
          store.commonStore.setShortFormLead(result.data);
          if (result.data.IsReentry) {
            rudderstackService.multiJourneyReentry();
          }
        } else {
          store.commonStore.setMapiErrorVariant(
            determineMapiErrorVariant(error)
          );
          rudderstackService.issue(
            `Short form error after ${attempts} attempts: ${error?.message || 'Unknown error'}`
          );
        }
      } catch (error) {
        store.commonStore.setMapiErrorVariant(determineMapiErrorVariant(error));
        rudderstackService.issue(String(error));
      } finally {
        store.commonStore.setButtonLoading(false);
      }
    }
  }

  public async sendFinishEvent(
    shortFormlead: ShortFormLeadResponse,
    firstName: string
  ) {
    sendEventToHost('finish', {
      data: {
        leadid: shortFormlead.LeadId,
        leadguid: shortFormlead.LeadGUID,
        firstName,
      },
    });
  }

  public async updateEngagement(
    formValues: any,
    status: string,
    engagementInfo: EngagementInfo,
    leadType: LeadType
  ) {
    const data = compileBaselineEngagementData(
      status,
      store.initializeStore.getVariant() || '',
      engagementInfo,
      formValues,
      leadType
    );
    await this.firebaseService.modifyEngagement(
      data,
      store.partnerStore.partner!,
      store.initializeStore.options.passthroughParams ?? {}
    );
    this.engagementEmitter.dispatchEvent(
      new CustomEvent('engagement_created', { detail: data })
    );
  }

  private waitForEngagementCreation() {
    return new Promise((resolve) => {
      const handler = (event: any) => {
        resolve(event.detail);
        this.engagementEmitter.removeEventListener(
          'engagement_created',
          handler
        );
      };

      this.engagementEmitter.addEventListener('engagement_created', handler);
    });
  }

  public async createRiskAssessment(token: string) {
    return this.cloudFunctionsService.createRiskAssessment({ token });
  }
}

export const eventService = new EventService();
